import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUpRight, Menu, X } from 'lucide-react';
import { useLang } from '@/context/LanguageContext';
import { SITE } from '@/constants/testIds';

export const Header = () => {
  const { t, lang, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const nav = [
    { href: '#products', label: t.nav.products, id: SITE.navProducts },
    { href: '#why', label: t.nav.why, id: SITE.navWhy },
    { href: '#clients', label: t.nav.clients, id: SITE.navClients },
    { href: '#contact', label: t.nav.contact, id: SITE.navContact },
  ];

  const goto = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'backdrop-blur-xl bg-[#050505]/80 border-b border-white/5'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1600px] mx-auto flex items-center justify-between px-6 md:px-12 h-20">
        <button
          data-testid={SITE.headerWordmark}
          onClick={() => goto('#top')}
          className="font-display text-3xl font-extrabold tracking-tighter leading-none"
        >
          Mads<span className="text-[#CCFF00]">.</span>
        </button>

        <nav className="hidden md:flex items-center gap-10">
          {nav.map((n) => (
            <button
              key={n.id}
              data-testid={n.id}
              onClick={() => goto(n.href)}
              className="link-underline font-mono text-xs uppercase tracking-[0.2em] text-white/70 hover:text-white transition-colors"
            >
              {n.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <button
            data-testid={SITE.langToggle}
            onClick={toggle}
            className="font-mono text-xs uppercase tracking-[0.2em] text-white/60 hover:text-[#CCFF00] transition-colors border border-white/15 hover:border-[#CCFF00] px-3 py-2"
          >
            {lang === 'tr' ? 'TR / EN' : 'EN / TR'}
          </button>
          <button
            data-testid={SITE.headerCta}
            onClick={() => goto('#contact')}
            className="hidden md:inline-flex items-center gap-2 bg-[#CCFF00] hover:bg-[#B3E600] text-black font-semibold uppercase tracking-wider text-xs px-5 py-3 transition-colors"
          >
            {t.nav.cta}
            <ArrowUpRight size={16} strokeWidth={2.5} />
          </button>
          <button
            onClick={() => setOpen((v) => !v)}
            className="md:hidden text-white p-2"
            aria-label="menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden overflow-hidden border-t border-white/5 bg-[#050505]/95 backdrop-blur-xl"
          >
            <div className="flex flex-col px-6 py-6 gap-4">
              {nav.map((n) => (
                <button
                  key={n.id}
                  onClick={() => goto(n.href)}
                  className="text-left font-display text-3xl font-bold text-white/90 hover:text-[#CCFF00]"
                >
                  {n.label}
                </button>
              ))}
              <button
                onClick={() => goto('#contact')}
                className="mt-2 inline-flex items-center gap-2 bg-[#CCFF00] text-black font-semibold uppercase tracking-wider text-xs px-5 py-4 w-fit"
              >
                {t.nav.cta}
                <ArrowUpRight size={16} strokeWidth={2.5} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
